import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Card, PageHeader, Stat, Btn, Pill } from "../components/UI";
import { BarChart, Bar, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid, Cell } from "recharts";
import { Download, TrendingUp, Eye, DollarSign, MapPin, MousePointerClick } from "lucide-react";

const PALETTE = ["var(--chart-1)", "var(--chart-2)", "var(--chart-3)", "var(--chart-4)", "var(--chart-5)", "var(--chart-6)"];
const fmt = (n) => { if (n >= 1e6) return (n / 1e6).toFixed(1) + "M"; if (n >= 1e3) return (n / 1e3).toFixed(1) + "K"; return String(n); };
const usd = (n) => "$" + new Intl.NumberFormat().format(Math.round(n || 0));

export default function Reports() {
  const [data, setData] = useState(null);
  useEffect(() => { (async () => { const { data } = await api.get("/reports/overview"); setData(data); })(); }, []);
  if (!data) return <div className="p-8 text-slate-400">Loading…</div>;
  const k = data.kpis;

  return (
    <div className="p-6 lg:p-8 space-y-6">
      <PageHeader
        eyebrow="Decision-ready dashboards"
        title="Reports"
        subtitle="Aggregated, privacy-safe performance across channels, audiences, geographies, and clients."
        action={<Btn variant="secondary" data-testid="export-report-btn"><Download className="w-4 h-4" /> Export PDF</Btn>}
      />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat label="Impressions" value={fmt(k.impressions)} change={18} icon={Eye} />
        <Stat label="Clicks" value={fmt(k.clicks)} change={12} icon={MousePointerClick} />
        <Stat label="Spend" value={usd(k.spend)} change={-4} icon={DollarSign} />
        <Stat label="Visits" value={fmt(k.visits)} change={22} icon={MapPin} />
      </div>

      <Card>
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="text-xs uppercase tracking-wider text-slate-400 font-medium">Channel performance</div>
            <h3 className="font-display text-xl font-bold mt-1">Budget allocation by channel</h3>
          </div>
          <Pill status="Active">Live</Pill>
        </div>
        {data.channel_mix.length === 0 ? <div className="py-10 text-center text-slate-500">No channel data yet</div> : (
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={data.channel_mix}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgb(var(--contrast-rgb) / 0.05)" />
              <XAxis dataKey="channel" stroke="var(--text-muted)" fontSize={11} />
              <YAxis stroke="var(--text-muted)" fontSize={11} tickFormatter={(v) => `$${fmt(v)}`} />
              <Tooltip contentStyle={{ background: "var(--bg-elevated)", border: "1px solid rgb(var(--ok-400) / 0.3)", borderRadius: 8 }} />
              <Bar dataKey="spend" radius={[6, 6, 0, 0]}>
                {data.channel_mix.map((_, i) => <Cell key={i} fill={PALETTE[i % PALETTE.length]} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        )}
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card>
          <div className="text-xs uppercase tracking-wider text-slate-400 font-medium">Delivery KPIs</div>
          <div className="mt-3 space-y-2 text-sm">
            <Row k="CTR" v={`${k.ctr}%`} />
            <Row k="Total budget" v={usd(k.total_budget)} />
            <Row k="Spend pacing" v={`${k.pacing}%`} highlight />
            <Row k="Cost per visit" v={usd(k.cpv)} />
            <Row k="Active campaigns" v={k.active_campaigns} />
          </div>
        </Card>
        <Card>
          <div className="text-xs uppercase tracking-wider text-slate-400 font-medium">Privacy & compliance</div>
          <div className="mt-3 space-y-2 text-sm text-slate-300">
            <div className="flex items-center gap-2"><TrendingUp className="w-4 h-4 text-cyan-300" /> All metrics aggregated to a minimum cohort threshold.</div>
            <div className="flex items-center gap-2"><TrendingUp className="w-4 h-4 text-cyan-300" /> Device-level data restricted from client view.</div>
            <div className="flex items-center gap-2"><TrendingUp className="w-4 h-4 text-cyan-300" /> GPP / CCPA / GDPR signals respected.</div>
            <div className="flex items-center gap-2"><TrendingUp className="w-4 h-4 text-cyan-300" /> Speed-limit fraud filtering active on geo events.</div>
          </div>
        </Card>
      </div>
    </div>
  );
}

const Row = ({ k, v, highlight }) => (
  <div className="flex justify-between border-b border-white/5 py-1.5">
    <span className="text-slate-400">{k}</span>
    <span className={`font-mono ${highlight ? "text-cyan-300" : "text-white"}`}>{v}</span>
  </div>
);
